import React from 'react';

import dayjs, { Dayjs } from 'dayjs';

import { styled, useTheme } from '@mui/material/styles';
import { DateCalendar, LocalizationProvider, PickersDay } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import 'dayjs/locale/pt-br';
import { useIsMobile, useIsTablet } from 'utils/mediaQueries';

dayjs.locale('pt-br');

const StyledDayWrapper = styled('div')<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  border: `1px solid ${theme.palette.primary.main}`,
  borderColor: theme.palette.primary.main,
  color: isSelected ? theme.palette.common.black : theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

interface CalendarProps {
  selectedDate: Dayjs | null;
  onDateChange: (value: Dayjs | null) => void;
  eventDates: string[];
}

const CalendarFilter: React.FC<CalendarProps> = ({ selectedDate, onDateChange, eventDates }) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const isEventDate = (date: Dayjs) => {
    return eventDates.includes(date.format('YYYY-MM-DD'));
  };

  const CustomDay = (props: any) => {
    const { day, selected, outsideCurrentMonth, ...other } = props;
    const isEvent = isEventDate(day);
    const formattedDay = day.format('YYYY-MM-DD');

    return (
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        sx={{
          borderRadius: '50%',
          '&.Mui-selected': {
            background: theme.palette.background.paper,
            color: theme.palette.common.black,
            '&:hover': {
              background: theme.palette.primary.main,
            },
          },
          '&.Mui-selected.MuiPickersDay-today': {
            borderColor: theme.palette.primary.main,
          },
          ...(isEvent && {
            border: `1px solid ${theme.palette.primary.main}`,
          }),
          color: selected ? theme.palette.common.black : theme.palette.text.primary,
          background: selected ? theme.palette.primary.main : theme.palette.background.default,

          '&:hover': {
            background: theme.palette.background.paper,
          },
        }}
      >
        {isEvent ? (
          <StyledDayWrapper isSelected={selected}>{day.date()}</StyledDayWrapper>
        ) : (
          day.date()
        )}
      </PickersDay>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
      <DateCalendar
        value={selectedDate}
        onChange={onDateChange}
        disablePast
        slots={{
          day: CustomDay,
        }}
        sx={{
          mt: isMobile ? 1 : 0,
          borderRadius: 2,
          transform: isMobile ? 'scale(1)' : isTablet ? 'scale(1)' : 'scale(1.1)',
          backgroundColor: 'background.paper',
          '& .MuiPickersArrowSwitcher-button': {
            color: `${theme.palette.primary.main} !important`,
          },
          '& .MuiSvgIcon-root': {
            color: `${theme.palette.primary.main} !important`,
          },
          '& .MuiDayCalendar-weekDayLabel': {
            color: `${theme.palette.text.primary} !important`,
            fontWeight: 'bold',
            fontSize: isMobile ? '0.8rem' : '1rem',
          },
          '& .MuiPickersCalendarHeader-label': {
            color: `${theme.palette.text.primary} !important`,
          },
          '& .MuiPickersYear-root': {
            color: `${theme.palette.primary.main} !important`,
          },
          '& .MuiPickersMonth-root': {
            color: `${theme.palette.primary.main} !important`,
          },
          '& .Mui-selected': {
            background: `${theme.palette.primary.main} !important`,
            color: theme.palette.common.black,
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default CalendarFilter;
